import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { formatCurrency, dollarsToCents } from "@/lib/format";
import { useState, useMemo, useEffect } from "react";
import { Plus, Trash2, Repeat, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/expenses")({
  head: () => ({ meta: [{ title: "Expenses — CEO Boardroom" }] }),
  component: ExpensesPage,
});

const CATEGORIES = ["Software", "Contractors", "Marketing", "Equipment", "Travel", "Education", "Fees", "Other"];

type Expense = {
  id: string;
  description: string;
  category: string | null;
  amount_cents: number;
  incurred_on: string;
  recurring: boolean;
  billing_cycle: string | null;
  notes: string | null;
};

function ExpensesPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);

  const { data: expenses = [] } = useQuery({
    queryKey: ["expenses", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from("expenses").select("*").eq("user_id", user!.id).order("incurred_on", { ascending: false });
      return (data ?? []) as Expense[];
    },
  });

  const stats = useMemo(() => {
    const now = new Date();
    const monthKey = now.toISOString().slice(0, 7);
    const yearKey = now.toISOString().slice(0, 4);
    const thisMonth = expenses.filter((e) => e.incurred_on.startsWith(monthKey)).reduce((s, e) => s + e.amount_cents, 0);
    const ytd = expenses.filter((e) => e.incurred_on.startsWith(yearKey)).reduce((s, e) => s + e.amount_cents, 0);
    // recurring monthly burn (normalize)
    const monthlyBurn = expenses.filter((e) => e.recurring).reduce((s, e) => {
      const mult = e.billing_cycle === "yearly" ? 1 / 12 : e.billing_cycle === "quarterly" ? 1 / 3 : 1;
      return s + e.amount_cents * mult;
    }, 0);
    return { thisMonth, ytd, monthlyBurn: Math.round(monthlyBurn), recurringCount: expenses.filter((e) => e.recurring).length };
  }, [expenses]);

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("expenses").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["expenses"] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold">Expenses</h1>
          <p className="text-sm text-muted-foreground mt-1">What it costs to run the business.</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[color:var(--gold)] text-[color:var(--primary-foreground)]">
          <Plus className="h-4 w-4 mr-1.5" /> New Expense
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Mini label="This Month" value={formatCurrency(stats.thisMonth)} />
        <Mini label="Year to Date" value={formatCurrency(stats.ytd)} />
        <Mini label="Monthly Burn (Recurring)" value={formatCurrency(stats.monthlyBurn)} />
        <Mini label="Recurring" value={`${stats.recurringCount} items`} />
      </div>

      {expenses.length === 0 ? (
        <div className="boardroom-card p-10 text-center">
          <p className="text-foreground/80 mb-2">No expenses logged.</p>
          <p className="text-sm text-muted-foreground">Track what leaves the business so you know your true margin.</p>
        </div>
      ) : (
        <div className="boardroom-card overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[10px] text-muted-foreground uppercase border-b border-[color:var(--hairline)]">
                <th className="px-4 py-3 font-semibold tracking-wider">Date</th>
                <th className="px-4 py-3 font-semibold tracking-wider">Description</th>
                <th className="px-4 py-3 font-semibold tracking-wider">Category</th>
                <th className="px-4 py-3 font-semibold tracking-wider text-right">Amount</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-[color:var(--hairline)]/60">
              {expenses.map((e) => (
                <tr key={e.id} className="group hover:bg-[color:var(--surface-2)]/40">
                  <td className="px-4 py-3 tabular text-muted-foreground text-xs">{new Date(e.incurred_on + "T00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" })}</td>
                  <td className="px-4 py-3">
                    <div className="text-foreground flex items-center gap-2">
                      {e.description}
                      {e.recurring && <Repeat className="h-3 w-3 text-[color:var(--teal)]" />}
                    </div>
                    {e.notes && <div className="text-[11px] text-muted-foreground mt-0.5">{e.notes}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{e.category ?? "—"}</td>
                  <td className="px-4 py-3 text-right tabular text-foreground">{formatCurrency(e.amount_cents)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button onClick={() => { setEditing(e); setOpen(true); }} className="opacity-100 md:opacity-0 md:group-hover:opacity-100 text-muted-foreground hover:text-foreground" aria-label="Edit">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => { if (confirm("Delete this expense?")) del.mutate(e.id); }} className="opacity-100 md:opacity-0 md:group-hover:opacity-100 text-muted-foreground hover:text-[color:var(--danger)]" aria-label="Delete">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ExpenseDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }} userId={user?.id} editing={editing} />
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div className="boardroom-card p-4">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-display text-xl font-semibold tabular text-[color:var(--gold)] mt-1">{value}</div>
    </div>
  );
}

function ExpenseDialog({ open, onOpenChange, userId, editing }: { open: boolean; onOpenChange: (v: boolean) => void; userId?: string; editing?: Expense | null }) {
  const qc = useQueryClient();
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("Software");
  const [incurredOn, setIncurredOn] = useState(new Date().toISOString().slice(0, 10));
  const [recurring, setRecurring] = useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open) {
      if (editing) {
        setDescription(editing.description);
        setAmount((editing.amount_cents / 100).toString());
        setCategory(editing.category ?? "Software");
        setIncurredOn(editing.incurred_on);
        setRecurring(editing.recurring);
        setBillingCycle(editing.billing_cycle ?? "monthly");
        setNotes(editing.notes ?? "");
      } else {
        setDescription(""); setAmount(""); setCategory("Software");
        setIncurredOn(new Date().toISOString().slice(0, 10));
        setRecurring(false); setBillingCycle("monthly"); setNotes("");
      }
    }
  }, [open, editing?.id]);

  async function save() {
    if (!userId || !description.trim()) return;
    const payload = {
      description: description.trim(),
      amount_cents: dollarsToCents(amount || "0"),
      category,
      incurred_on: incurredOn,
      recurring,
      billing_cycle: recurring ? billingCycle : null,
      notes: notes.trim() || null,
    };
    if (editing) {
      const { error } = await supabase.from("expenses").update(payload).eq("id", editing.id);
      if (error) return toast.error(error.message);
      toast.success("Expense updated");
    } else {
      const { error } = await supabase.from("expenses").insert({ user_id: userId, ...payload });
      if (error) return toast.error(error.message);
      toast.success("Expense added");
    }
    qc.invalidateQueries({ queryKey: ["expenses"] });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[color:var(--surface-1)] border-[color:var(--hairline)] max-w-md">
        <DialogHeader><DialogTitle>{editing ? "Edit Expense" : "New Expense"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs mb-1.5 block">Description</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} autoFocus /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs mb-1.5 block">Amount</Label>
              <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" step="0.01" className="pl-7 tabular" />
              </div>
            </div>
            <div><Label className="text-xs mb-1.5 block">Date</Label><Input type="date" value={incurredOn} onChange={(e) => setIncurredOn(e.target.value)} /></div>
          </div>
          <div><Label className="text-xs mb-1.5 block">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="rec" checked={recurring} onCheckedChange={(v) => setRecurring(!!v)} />
            <Label htmlFor="rec" className="text-sm cursor-pointer">Recurring subscription</Label>
          </div>
          {recurring && (
            <div><Label className="text-xs mb-1.5 block">Billing cycle</Label>
              <Select value={billingCycle} onValueChange={setBillingCycle}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div><Label className="text-xs mb-1.5 block">Notes (optional)</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          <Button onClick={save} className="w-full bg-[color:var(--gold)] text-[color:var(--primary-foreground)]">{editing ? "Save changes" : "Add Expense"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
